import {
  type FC,
  type HTMLAttributes,
  useEffect,
  useRef,
  useState,
} from "react";

import { clsx, Icon } from "@alpacahq/ui/old";

export enum CheckboxState {
  Checked = "checked",
  Unchecked = "unchecked",
  Indeterminate = "indeterminate",
}

export interface CheckboxProps
  extends Omit<HTMLAttributes<HTMLDivElement>, "onChange"> {
  defaultState?: CheckboxState;
  state?: CheckboxState;
  onChange?: (state: CheckboxState) => void;
  disabled?: boolean;
}

export const Checkbox: FC<CheckboxProps> = ({
  defaultState = CheckboxState.Unchecked,
  state: externalState,
  onChange,
  disabled = false,
  ...props
}) => {
  const checkboxRef = useRef(null);

  const [internalState, setInternalState] = useState(defaultState);
  const [focused, setFocused] = useState(false);

  const state = externalState !== undefined ? externalState : internalState;

  useEffect(() => {
    setInternalState(defaultState);
  }, [defaultState]);

  const handleStateChange = () => {
    if (disabled) {
      return;
    }

    let nextState: CheckboxState;

    switch (state) {
      case CheckboxState.Unchecked:
        nextState = CheckboxState.Checked;
        break;
      case CheckboxState.Checked:
        nextState = CheckboxState.Indeterminate;
        break;
      default:
        nextState = CheckboxState.Unchecked;
    }

    setInternalState(nextState);

    if (onChange) {
      onChange(nextState);
    }
  };

  // default classes
  const baseClass = clsx(
    "flex h-4 min-h-4 w-4 min-w-4 cursor-pointer items-center justify-center rounded-[5px]",
    disabled && "opacity-50",
  );

  // state-specific classes for the checkbox
  const stateClass = clsx(
    state === CheckboxState.Unchecked &&
      "border border-zinc-300 bg-white focus:ring-yellow-400",
    state !== CheckboxState.Unchecked &&
      "border border-yellow-400 bg-yellow-400 text-yellow-900",
  );

  return (
    <div
      ref={checkboxRef}
      onClick={handleStateChange}
      onBlur={() => setFocused(false)}
      onFocus={() => setFocused(true)}
      className={clsx(baseClass, stateClass)}
      style={{
        boxShadow:
          focused && state !== CheckboxState.Unchecked
            ? "0 0 0 4px #FEF08A"
            : undefined,
      }}
      {...props}
    >
      {state === CheckboxState.Checked && (
        <Icon name="Check" className="h-3 w-3 stroke-[4px]" />
      )}
      {state === CheckboxState.Indeterminate && (
        <Icon name="MinusSmall" className="h-3 w-3 stroke-[4px]" />
      )}
    </div>
  );
};

export default Checkbox;
